<template>
  <table-column-picker
    v-slot="{ headers, activatorProps }"
    :headers="documentListHeaders"
    storage-key="documents-signature-table"
    title="Налаштування відображення переліку документів">
    <b-server-table
      :key="renderKey"
      v-model="selectedDocuments"
      :filter-props="{
        filterMap: [
          { key: 'search' },
          {
            key: 'typeId',
            directory: 'documentTypes',
            component: 'select',
          },
        ],
        scope: 'document',
      }"
      :headers="headers"
      :request-func="getDocuments"
      show-select
      @click:row="
        $router.push({
          name: 'document-signature-single',
          params: { id: $event.id },
        })
      ">
      <template #action-button>
        <div class="d-flex align-center ga-3">
          <table-column-picker-button v-bind="activatorProps" />
          <v-fade-transition>
            <v-btn
              v-if="selectedDocuments.length"
              class="mr-2"
              rounded="lg"
              @click.stop="signDocuments">
              Підписати документи
            </v-btn>
          </v-fade-transition>
          <v-btn
            v-if="can('document-signatures.create')"
            color="primary"
            rounded="lg"
            @click.stop="createDocument">
            <v-icon class="mr-1">mdi-plus</v-icon>
            Створити документ
          </v-btn>
        </div>
      </template>
      <template #item.created_at="{ item }">
        <span style="white-space: nowrap">
          {{ new CustomDate(item.created_at).toString({ time: true }) }}
        </span>
      </template>
      <template #item.type="{ item }">
        {{ item.type?.name }}
      </template>
      <template #item.name="{ item }">
        <v-tooltip :text="item?.name">
          <template #activator="{ props }">
            <div class="text-truncate" style="width: 250px" v-bind="props">
              <span>
                {{ item.name }}
              </span>
            </div>
          </template>
        </v-tooltip>
      </template>
      <template #item.number="{ item }">
        <span style="white-space: nowrap">
          {{ item.number }}
        </span>
      </template>
      <template #item.status="{ item }">
        <span style="white-space: nowrap">
          {{ item.status?.name }}
        </span>
      </template>
      <template #item.author="{ item }">
        <span style="white-space: nowrap">
          {{ getPersonName(item.author, { initials: true }) }}
        </span>
      </template>
      <template #[`item.contractors`]="{ item }">
        <v-menu>
          <template #activator="{ props }">
            <span v-if="item.contractors.length" class="link" v-bind="props">
              Показати
            </span>
            <span v-else>Відсутні</span>
          </template>
          <v-card>
            <v-card-text>
              <div
                v-for="(contractor, idx) in item.contractors"
                :key="contractor.id"
                :class="{ 'mb-2': item.contractors.length - 1 > idx }"
                class="link"
                @click.stop="
                  $router.push({
                    name: 'single-contractor',
                    params: { id: contractor?.contractorId },
                  })
                ">
                {{ contractor.contractor.name }}
              </div>
            </v-card-text>
          </v-card>
        </v-menu>
      </template>
      <template #bottom></template>
    </b-server-table>
  </table-column-picker>
</template>

<script lang="ts">
import { BServerTable } from 'best-modules/components'
import { CustomDate, getPersonName } from 'best-modules/utils'
import { axios, useRoute, useStore } from '@/plugins'
import { documentListHeaders } from '@/utils/tableHeaders'
import { useCan } from '@/composables/useCan'
import { computed, ref } from 'vue'
import {
  urlDocumentSignatureList,
  urlGetDiiaData,
  urlSignCreate,
} from '@/utils/urls'
import { PaginationList } from 'best-modules/components/server-table/types'
import { DocumentOld } from '@/utils/types/document'
import { openDialog } from '@/dialog'
import { useRouter } from 'vue-router'
import { setSnackbar } from 'best-modules/plugins'
import TableColumnPicker from '@/components/TableColumnPicker.vue'
import TableColumnPickerButton from '@/components/buttons/TableColumnPickerButton.vue'
import {
  DiiaRequestPayload,
  SignedDocument,
} from '@/dialog/components/sign-document/types'

export default {
  name: 'DocumentSignatureList',
  components: { TableColumnPickerButton, TableColumnPicker, BServerTable },
  data() {
    return { documentListHeaders }
  },
  methods: { CustomDate, getPersonName },
  setup() {
    const renderKey = ref(1)
    const route = useRoute()
    const router = useRouter()
    const store = useStore()
    const { can } = useCan()
    const userId = computed(() => store.state.user.userData?.id)

    const documentList = ref<any[]>([])
    const selectedDocuments = ref<any[]>([])

    const url = computed(() => {
      switch (route.name) {
        case 'document-signature-notes':
          return (query = {}) =>
            urlDocumentSignatureList({
              ...query,
              statusId: 1,
            })
        case 'document-signature-all':
          return (query = {}) =>
            urlDocumentSignatureList({
              ...query,
              statusId: 2, // done
            })
        case 'document-signature-signed':
          return (query = {}) =>
            urlDocumentSignatureList({
              ...query,
              statusId: 3, // done
            })
        case 'document-signature-canceled':
          return (query = {}) =>
            urlDocumentSignatureList({
              ...query,
              statusId: 4, // done
            })
        default:
          return (query: any) => {
            urlDocumentSignatureList(query)
          }
      }
    })

    const getDocuments = (query: any): Promise<PaginationList<DocumentOld>> => {
      return axios.get(url.value(query)).then(res => {
        documentList.value = res.data.data

        return res.data
      })
    }

    const createDocument = () => {
      openDialog({
        name: 'DocumentSignature',
        action: 'create',
        params: {
          cb: document => {
            router.push({
              name: 'document-signature-single',
              params: { id: document.id },
            })
          },
          submitLabel: 'Створити',
          hideActionButtons: true,
        },
      })
    }

    const signDocuments = () => {
      const canSign = (document: any) => {
        return (
          [1, 2].includes(document.statusId) &&
          document.users?.find((u: any) => u.userId === userId.value)
            ?.isSignature
        )
      }
      const documents = documentList.value.filter(d => {
        return selectedDocuments.value.includes(d.id) && canSign(d)
      })

      if (!documents.length) {
        return setSnackbar({
          color: 'warning',
          text: 'Жоден з обраних документів не можна підписати',
        })
      }

      return openDialog({
        name: 'SignDocument',
        dialogData: {
          documents: documents.flatMap(d => {
            return (d.document || []).map((file: any) => {
              return {
                ...file,
                id: String(d.id),
                uploadFilesParams: {
                  mainFolder: 'document',
                  nameFolder: 'signature',
                },
              }
            })
          }),
          diiaRequest: ({ algo, hash }: DiiaRequestPayload) => {
            return axios.post(urlGetDiiaData(documents[0].id), { algo, hash })
          },
        },
        params: {
          hideActionButtons: true,
          cb: async signatures => {
            if (Array.isArray(signatures)) {
              for (const signature of signatures as SignedDocument[]) {
                const sourceDocument = documents.find(d => {
                  return d.document?.some((file: any) => {
                    return file.path === signature.document.path
                  })
                })

                if (!sourceDocument) {
                  continue
                }

                await axios.post(urlSignCreate(), {
                  documentId: sourceDocument.id,
                  documentSigned: signature.documentSigned,
                  signingDate: signature.signingDate,
                  fullCertificate: signature.fullCertificate,
                })
              }
            }

            renderKey.value += 1
          },
        },
      })
    }

    return {
      getDocuments,
      createDocument,
      can,
      renderKey,
      signDocuments,
      selectedDocuments,
    }
  },
}
</script>

<style lang="scss" scoped></style>
